import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, TextField, Button, Alert } from '@mui/material';
import { exportToExcel } from '../excel_export'; // <-- Add this import

export default function ResultsList({ results = [], error }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [reverse, setReverse] = useState(false);

  // Ensure results are not empty initially
  const initialResults = results.length > 0 ? results : [];

  if (!initialResults.length) {
    return null;
  }

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleReverse = () => {
    setReverse(!reverse);
  };

  const filteredResults = initialResults.filter(person =>
    person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.matchedSkills.some(skill => skill.skill_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const sortedResults = [...filteredResults].sort((a, b) => {
    if (!sortConfig.key) return 0;
    if (sortConfig.key === 'proficiency_level') {
      const aValue = a.matchedSkills[0]?.proficiency_level || 0;
      const bValue = b.matchedSkills[0]?.proficiency_level || 0;
      return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
    }
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
    return 0;
  });

  const finalResults = reverse ? sortedResults.reverse() : sortedResults;

  const rows = finalResults.flatMap(person =>
    (person.matchedSkills && person.matchedSkills.length > 0 ? person.matchedSkills : person.skills).map(skill => ({
      name: person.name,
      skill_name: skill.skill_name,
      proficiency_level: skill.proficiency_level
    }))
  );

  return (
    <Box maxWidth={900} mx="auto">
      <Box
        mb={2}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          background: '#f5f7fa',
          borderRadius: 2,
          p: { xs: 1, sm: 2 },
          boxShadow: 1,
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          minWidth: 0,
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <Box flex={1} minWidth={0} display="flex" alignItems="center">
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: '100%',
              minWidth: 0,
              background: 'white',
              borderRadius: 1,
              boxShadow: 3,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              '& .MuiInputBase-root': {
                height: '40px',
                minHeight: 0,
                fontSize: { xs: '1rem', sm: '1.1rem' },
              },
            }}
            inputProps={{ style: { padding: '10px 12px' } }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap" minWidth={0}>
          <Button
            onClick={() => handleSort('name')}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: 1,
              textTransform: 'none',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              px: 2.5,
              py: 1,
              minWidth: 0,
              height: '40px',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Sort by Name
          </Button>
          <Button
            onClick={() => handleSort('proficiency_level')}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: 1,
              textTransform: 'none',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              px: 2.5,
              py: 1,
              minWidth: 0,
              height: '40px',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Sort by Proficiency
          </Button>
          <Button
            onClick={handleReverse}
            variant="contained"
            color="secondary"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: 1,
              textTransform: 'none',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              px: 2.5,
              py: 1,
              minWidth: 0,
              height: '40px',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Reverse
          </Button>
          {/* Download Excel Button */}
          <Button
            onClick={() => exportToExcel(rows, "search_results.xlsx")}
            variant="contained"
            color="success"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: 1,
              textTransform: 'none',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              px: 2.5,
              py: 1,
              minWidth: 0,
              height: '40px',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
            }}
            disabled={rows.length === 0}
          >
            Download
          </Button>
        </Box>
      </Box>
      {error ? (
        <Box maxWidth={900} textAlign="center" mt={2} mb={2}>
          <Alert severity="error" sx={{ justifyContent: 'center' }}>{error}</Alert>
        </Box>
      ) : rows.length > 0 ? (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'rgba(86,204,242,0.15)' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Employee Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Skill</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Proficiency</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={row.name + row.skill_name + row.proficiency_level + idx}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.skill_name}</TableCell>
                  <TableCell>{row.proficiency_level}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
}